import { NavLink, useNavigate, useParams } from "react-router";
import { ReactNode, useCallback } from "react";
import { useRelationshipsRelationshipShowQuery } from "@/services/backend/relationships/api/service";
import {
  CustomerPageMain,
  CustomerPageMainContent,
  CustomerPageMainHeader,
} from "@/routes/customers/[customerId]/_components/customer-page";
import { CustomerPageBreadcrumbs } from "@/routes/customers/[customerId]/_components/customer-page-breadcrumbs";
import t from "@/lang/lang";
import { ServiceObjectName } from "@/services/backend/customers/components/service-object-name";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Commitment,
  Relationship,
} from "@/services/backend/relationships/types/relationship";
import { RelationshipCard } from "@/services/backend/relationships/components/relationship-card";
import { H3 } from "@/shared/components/typography";
import { CommitmentsCard } from "@/services/backend/relationships/components/commitments-card";
import { ContractCard } from "@/services/backend/relationships/components/contract-card";
import { DeleteRelationshipDialog } from "@/services/backend/relationships/components/delete-relationship-dialog";

export function RelationshipRoute() {
  const { customerId, relationshipId } = useParams();

  const { data: relationship, error } = useRelationshipsRelationshipShowQuery({
    id: relationshipId!,
  });

  const navigate = useNavigate();

  return (
    <CustomerPageMain>
      <CustomerPageMainHeader>
        <CustomerPageBreadcrumbs
          breadcrumbs={[
            {
              to: `/customers/${customerId}/relationships`,
              label: t("Beziehungen"),
            },
            {
              to: `/customers/${customerId}/relationships/${relationshipId}`,
              label: <ServiceObjectName id={relationship?.serviceObjectId} />,
            },
          ]}
        />
      </CustomerPageMainHeader>
      <CustomerPageMainContent className="gap-4">
        {error && <RTKQueryErrorAlert error={error} />}
        <div className="grid grid-cols-[1fr_auto] gap-2">
          <H3>
            <span>{t("Beziehung mit Objekt: ")}</span>
            <ServiceObjectName id={relationship?.serviceObjectId} />
          </H3>
          <div className="h-4">
            <DeleteRelationshipDialog
              relationshipId={relationshipId!}
              onSuccess={() =>
                navigate(`/customers/${customerId}/relationships`)
              }
            />
          </div>
        </div>
        {relationship && <Page relationship={relationship} />}
      </CustomerPageMainContent>
    </CustomerPageMain>
  );
}

function Page({ relationship }: { relationship: Relationship }) {
  const renderCommitmentLink = useCallback(
    (commitment: Commitment, rel: Relationship, children: ReactNode) => (
      <NavLink
        to={`/customers/${rel.customerId}/relationships/${commitment.relationshipId}/commitments/${commitment.id}`}
      >
        {children}
      </NavLink>
    ),
    [],
  );

  return (
    <div className="grid gap-2">
      <RelationshipCard relationship={relationship} />
      <ContractCard />
      <CommitmentsCard
        relationship={relationship}
        renderCommitmentLink={renderCommitmentLink}
        renderDocumentLink={(path, children) => (
          <NavLink to={path}>{children}</NavLink>
        )}
      />
    </div>
  );
}
